import {Box, Button, Group, Modal} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import UnicodeCharacterChooser from 'notebook/mediocre/symbols/UnicodeCharacterChooser';

import isCharBlocked from './isCharBlocked';

interface InsertSymbolModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (symbol: string) => void;
}

function InsertSymbolModal({onRequestClose, onRequestSubmit}: InsertSymbolModalProps): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [symbol, setSymbol] = useState<string | null>(null);

  const onSubmit = useCallback(() => {
    if (symbol == null || isSubmitting) {
      // If there's no symbol, don't submit the modal. The footer button should be disabled, but there are
      // various ways to submit the form (in Safari, pressing enter on the text field submits the form, even
      // if the submit button is disabled).
      return;
    }
    setIsSubmitting(true);
    onRequestSubmit(symbol);
    onRequestClose();
  }, [symbol, isSubmitting, onRequestSubmit, onRequestClose]);

  const onSelectSymbol = useCallback(
    ({char, insert}: Parameters<React.ComponentProps<typeof UnicodeCharacterChooser>['selectChar']>[0]) => {
      if (isCharBlocked(char) || isSubmitting) {
        return;
      }
      setSymbol(char);
      if (insert) {
        onSubmit();
      }
    },
    [isSubmitting, setSymbol, onSubmit]
  );

  return (
    <Modal opened={true} onClose={onRequestClose} title="Insert symbol">
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <UnicodeCharacterChooser char={symbol} selectChar={onSelectSymbol} />
        <Group position="right" mt="md">
          <Button type="submit" disabled={symbol == null || isSubmitting}>
            Insert
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}

export default InsertSymbolModal;
